<template>
  <v-card 
    class="article-card" 
    elevation="1" 
    height="100%" 
    @click="navigateToArticle"
  >
    <!-- Image de l'article -->
    <div class="article-card-image">
      <!-- Afficher un label si présent (hot, trending, recommended) -->
      <div v-if="label" class="article-label" :class="label">
        {{ labelText }}
      </div>
      
      <!-- Image de couverture avec aspect ratio adapté à la taille de carte -->
      <!-- Utiliser notre composant personnalisé au lieu de v-img -->
      <FixedImage
        class="article-image"
        :src="getArticleImage()"
        :height="getImageHeight()"
        :aspect-ratio="getImageRatio()"
        cover
        eager
      >
        <!-- Overlay pour grandes cartes -->
        <div v-if="isLargeCard" class="d-flex align-center justify-center fill-height">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </FixedImage>
    </div>
    
    <!-- Contenu de l'article -->
    <v-card-text class="article-card-content pa-3">
      <!-- Catégorie (visible seulement sur les cartes grandes et moyennes) -->
      <div v-if="showCategory" class="article-category text-caption text-uppercase mb-1" :class="'category-'+article.category">
        {{ getCategoryName(article.category) }}
      </div>
      
      <!-- Source -->
      <div class="article-source text-caption text-medium-emphasis">
        <v-icon size="x-small" color="primary" class="mr-1">mdi-circle-small</v-icon>
        {{ article.sourceName }}
      </div>
      
      <!-- Titre -->
      <h3 
        class="article-title mt-1" 
        :class="getTitleClass()"
      >
        {{ article.title }}
      </h3>
      
      <!-- Description (visible seulement sur les cartes grandes) -->
      <p v-if="showDescription && article.description" class="article-description text-caption text-medium-emphasis mt-2">
        {{ getFormattedDescription() }}
      </p>
      
      <!-- Méta-informations -->
      <div class="article-meta d-flex justify-space-between text-caption text-medium-emphasis mt-3">
        <div class="article-time">
          <v-icon size="x-small" class="mr-1">mdi-clock-outline</v-icon>
          {{ formatDate(article.publishedAt) }}
        </div>
        
        <div v-if="article.viewCount" class="article-views">
          <v-icon size="x-small" class="mr-1">mdi-eye-outline</v-icon>
          {{ article.viewCount }}
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import FixedImage from '../common/FixedImage.vue';

export default {
  name: 'ArticleCard',
  components: {
    FixedImage
  },
  props: {
    article: {
      type: Object,
      required: true
    },
    label: {
      type: String,
      default: null,
      validator: value => ['hot', 'trending', 'recommended', null].includes(value)
    },
    size: {
      type: String,
      default: 'small',
      validator: value => ['small', 'medium', 'large'].includes(value)
    }
  },
  data() {
    return {
      // Observer pour détecter quand la carte est visible
      observer: null,
      isVisible: false
    }
  },
  computed: {
    // Texte pour le label
    labelText() {
      const labels = {
        'hot': 'HOT',
        'trending': 'TRENDING',
        'recommended': 'POUR VOUS'
      }
      return labels[this.label] || ''
    },
    
    // Détermine si la carte est grande (pour l'affichage spécial)
    isLargeCard() {
      return this.size === 'large';
    },
    
    // Détermine si la carte est moyenne
    isMediumCard() {
      return this.size === 'medium';
    },
    
    // Détermine si on doit afficher la catégorie
    showCategory() {
      return this.isLargeCard || this.isMediumCard;
    },
    
    // Détermine si on doit afficher la description
    showDescription() {
      return this.isLargeCard;
    }
  },
  mounted() {
    // Configurer l'observateur d'intersection pour détecter la visibilité
    this.setupIntersectionObserver();
  },
  beforeUnmount() {
    // Nettoyer l'observateur lorsque le composant est détruit
    this.cleanupIntersectionObserver();
  },
  methods: {
    // Formater la date de publication
    formatDate(dateString) {
      if (!dateString) return '';
      
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / (1000 * 60));
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffMins < 60) {
        return `Il y a ${diffMins}min`;
      } else if (diffHours < 24) {
        return `Il y a ${diffHours}h`;
      } else if (diffDays < 30) {
        return `Il y a ${diffDays}j`;
      } else {
        // Format date précise pour les articles plus anciens
        const d = date.getDate();
        const m = date.getMonth() + 1;
        return `${d < 10 ? '0' + d : d}/${m < 10 ? '0' + m : m}/${date.getFullYear()}`;
      }
    },
    
    // Nom de la catégorie à partir de son identifiant
    getCategoryName(category) {
      const categories = {
        'politique': 'Politique',
        'economie': 'Économie',
        'technologie': 'Tech',
        'sante': 'Santé',
        'sport': 'Sport',
        'culture': 'Culture',
        'international': 'International',
        'autres': 'Autres'
      };
      return categories[category] || 'Catégorie';
    },
    
    // Obtenir l'image de l'article selon la taille ou une image placeholder
    getArticleImage() {
      if (!this.article) return '';
      
      // Priorité aux images locales traitées
      if (this.isLargeCard && this.article.localImageLarge) {
        return this.article.localImageLarge;
      } else if (this.isMediumCard && this.article.localImageMedium) {
        return this.article.localImageMedium;
      } else if (this.article.localImageSmall) {
        return this.article.localImageSmall;
      } else if (this.article.localImageUrl) {
        return this.article.localImageUrl;
      } else if (this.article.imageUrl) {
        return this.article.imageUrl;
      }
      
      // Fallback sur placeholder
      const id = this.article._id || Math.random().toString(36).substring(2, 15);
      return `https://picsum.photos/400/225?random=${id}`;
    },
    
    // Obtenir la hauteur de l'image selon la taille de la carte
    getImageHeight() {
      if (this.isLargeCard) return 300;
      if (this.isMediumCard) return 225;
      return 180;
    },
    
    // Obtenir le ratio d'image selon la taille de la carte
    getImageRatio() {
      return this.isLargeCard ? 21/9 : 16/9;
    },
    
    // Obtenir la classe pour le titre selon la taille de la carte
    getTitleClass() {
      if (this.isLargeCard) return 'text-h6 font-weight-medium';
      if (this.isMediumCard) return 'text-subtitle-1 font-weight-medium';
      return 'text-body-2 font-weight-medium';
    },
    
    // Formater la description (tronquer si trop longue)
    getFormattedDescription() {
      if (!this.article.description) return '';
      
      const maxLength = 120;
      if (this.article.description.length <= maxLength) {
        return this.article.description;
      }
      
      return this.article.description.substring(0, maxLength) + '...';
    },
    
    // Navigation vers la page de l'article
    navigateToArticle(event) {
      // Émettre l'événement click avant la navigation
      this.$emit('click');
      
      // Vérifier que l'article existe
      if (!this.article) {
        console.error('Erreur: article manquant lors de la navigation');
        return;
      }
      
      // Prévenir la navigation par défaut si présente
      if (event) {
        event.preventDefault();
      }
      
      // Utiliser nextTick pour s'assurer que les événements sont traités avant la navigation
      this.$nextTick(() => {
        try {
          let path;
          
          // Préférer le slug si disponible, sinon utiliser l'ID
          if (this.article.slug) {
            path = `/${this.article.slug}`;
          } else if (this.article._id) {
            path = `/article/${this.article._id}`;
          } else {
            console.error('Ni slug ni ID disponible pour l\'article');
            return;
          }
          
          // Router push avec catch pour gérer toute erreur
          this.$router.push(path).catch(err => {
            if (err.name !== 'NavigationDuplicated') {
              console.error('Erreur de navigation:', err);
            }
          });
        } catch (error) {
          console.error('Erreur lors de la navigation vers l\'article:', error);
        }
      });
    },
    
    // Configurer l'observateur d'intersection pour détecter quand la carte est visible
    setupIntersectionObserver() {
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && !this.isVisible) {
            this.isVisible = true;
            // Émettre l'événement de vue quand la carte devient visible
            this.$emit('view');
          }
        }, {
          threshold: 0.5 // Émettre quand l'élément est au moins 50% visible
        });
        
        this.observer.observe(this.$el);
      }
    },
    
    // Nettoyer l'observateur d'intersection
    cleanupIntersectionObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.article-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 3px 3px -2px rgba(0,0,0,0.2), 
                0 3px 4px 0 rgba(0,0,0,0.14),
                0 1px 8px 0 rgba(0,0,0,0.12) !important;
    
    .article-image {
      transform: scale(1.05);
    }
    
    .article-title {
      color: var(--v-primary-base);
    }
  }
  
  .article-card-image {
    position: relative;
    overflow: hidden;
    
    .article-image {
      transition: transform 0.3s ease;
    }
  }
  
  .article-label {
    position: absolute;
    top: 12px;
    left: 12px;
    font-size: 0.6875rem;
    font-weight: 500;
    text-transform: uppercase;
    padding: 4px 8px;
    border-radius: 4px;
    z-index: 1;
    
    &.hot {
      background-color: #F44336;
      color: white;
    }
    
    &.trending {
      background-color: #FFC107;
      color: rgba(0, 0, 0, 0.87);
    }
    
    &.recommended {
      background-color: #4CAF50;
      color: white;
    }
  }
  
  .article-category {
    display: inline-block;
    font-size: 0.75rem;
    font-weight: 500;
    padding: 2px 6px;
    border-radius: 3px;
    margin-bottom: 4px;
    
    &.category-politique { background-color: #F44336; color: white; }
    &.category-economie { background-color: #4CAF50; color: white; }
    &.category-sport { background-color: #FFC107; color: rgba(0, 0, 0, 0.87); }
    &.category-international { background-color: #2196F3; color: white; }
    &.category-technologie { background-color: #9C27B0; color: white; }
    &.category-culture { background-color: #FF9800; color: white; }
    &.category-sante { background-color: #00BCD4; color: white; }
    &.category-autres { background-color: #757575; color: white; }
  }
  
  .article-card-content {
    flex-grow: 1;
    display: flex;
    flex-direction: column;
  }
  
  .article-title {
    line-height: 1.4;
    margin-bottom: 8px;
    flex-grow: 1;
    transition: color 0.3s ease;
  }
  
  .article-description {
    line-height: 1.5;
    opacity: 0.8;
  }
  
  .article-meta {
    margin-top: auto;
  }
}
</style>